'use client';
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  ShieldCheck,
  Phone,
  Clock,
  FileText,
  ChevronRight,
  Lock,
  Eye,
  CheckCircle,
} from 'lucide-react';
import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function PrivacyPolicyPage() {
  const { phoneNumber, showPhoneNumber } = useSiteSettings();
  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-[120px] pb-20 bg-slate-950 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 z-0" />
        <div className="absolute top-[-100px] right-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none z-0" />

        <div className="relative z-10 max-w-[1170px] mx-auto px-6 w-full text-left">
          <div
            style={{ height: '30px' }}
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 w-fit backdrop-blur-md"
          >
            PRIVACY ASSURED
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none mb-4">
            Privacy{' '}
            <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
              Policy
            </span>
          </h1>
          <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
            Your data protection is our highest priority. Read our terms on information safety,
            collection practices, and user choice.
          </p>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="flex-grow max-w-[1170px] mx-auto px-6 py-16 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Main policy body (Left) */}
          <div className="lg:col-span-8 text-left space-y-8 animate-fadeInUp">
            {/* Intro */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-5.5 h-5.5 text-accent" />
                Introduction
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                Faremaps and Faremaps.com fulfill, maintain, and secure the personalized information
                provided to us by our Customers/Travelers on different occasions at the time of
                flight booking or utilizing other travel services. The Privacy Policy Statement
                written here describes the manner in which we are going to treat the critical and
                important personal information of the Customer/Traveler as he/she visits our website
                for travel services.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed font-bold">
                It is recommended and urged that the Customer/Traveler should review the Privacy
                Policy and its content to understand and analyze the information on Privacy
                Protection.
              </p>
              <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-2xl text-[11px] font-bold text-blue-800 leading-normal">
                ⚠️ <strong>Note:</strong> We are also likely to offer travel services and/or collect
                personal information from co-branded sites. However, these sites do not adhere to
                our privacy policies in any way. We recommend checking the privacy statement of each
                co-branded site.
              </div>
            </div>

            {/* Our Pledge */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <Lock className="w-5.5 h-5.5 text-accent" />
                Pledge on Privacy and Information Security
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                Customer/Traveler information is the foundation and the essence of providing elite
                travel services. We undertake serious steps to maintain the information of the
                Customer/Traveler and secure it in every possible way. Here is the pledge that we
                take in our Privacy Policy Statement:
              </p>

              <ul className="space-y-4">
                {[
                  'We treat the information of our Customers/Travelers as very sensitive and confidential in nature and therefore safeguard it in every respect.',
                  'We will only use the personal information of our Customers/Travelers to deliver exclusive travel services and other opportunities in travel.',
                  'We only allow experienced travel agents and customer service representatives to wield the personal information for various business purposes. Agents and representatives who violate and/or do not adhere to the Privacy Promise shall have to go through strict disciplinary action.',
                  'We guarantee that the information of the Customer/Traveler will not be shared to any third party unless the Customer/Traveler is informed about it beforehand, in accordance with the agreements and legal protocols.',
                  'In only a few incidents, we may share the personal information of the Customer/Traveler with reputable travel companies and only when the Customer/Traveler has shown serious interest in their products. In this, the "Pledge to Privacy" SHALL NOT APPLY TO THE SERVICES OFFERED BY THESE REPUTABLE TRAVEL COMPANIES.',
                  'When Reputable Companies are hired by us for offering exclusive Travel Services to the Customers/Travelers, we ensure that ALL SUCH REPUTABLE COMPANIES adhere to the Privacy Standards of Faremaps and Faremaps.com.',
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex gap-3 items-start text-slate-600 text-xs font-semibold leading-relaxed"
                  >
                    <CheckCircle className="w-4.5 h-4.5 text-accent flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="p-4 bg-slate-50 border border-slate-200/60 rounded-2xl text-[11px] font-semibold text-slate-500 leading-normal">
                ℹ️ <strong>Information Verification:</strong> All legit attempts are made to secure
                and update the information of the Customer/Traveler. We also ensure
                Customer/Traveler information remains updated and most recent. We would also inform
                the Customers/Travelers on when/where the information will be accessed. If there is
                any flaw or error in the information, we will undertake stringent audit measures to
                correct those flaws/errors.
              </div>
            </div>

            {/* Collection details */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <Eye className="w-5.5 h-5.5 text-accent" />
                Collecting and Collating Personal Information
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed mb-4">
                The personal information of the Customer/Traveler is collected at the time he/she
                visits our website. Such information would include:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Full Name',
                  'Mailing Address',
                  'Credit Card & Billing Details',
                  'Date of Birth',
                  'E-mail Address',
                  'Telephone Number',
                  'IP Address & Device Identifiers',
                ].map((info) => (
                  <div
                    key={info}
                    className="bg-slate-50 border border-slate-200/40 rounded-xl p-3 text-xs font-bold text-slate-700 flex items-center gap-2.5"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                    {info}
                  </div>
                ))}
              </div>
            </div>

            {/* Minors */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                Personal Information of Minors
              </h2>
              <ul className="space-y-4 text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                <li>
                  We will not collect or save the personal information of children of age 12 years
                  or under. We will also not encourage minors or children under the age of 12 to
                  provide us with the information or make any kind of data submissions.
                </li>
                <li>
                  The minors falling within the age of 12 and 17 years need formal and written
                  permission of Parents/Guardians before booking flight tickets.
                </li>
                <li>
                  The Information of the Customers/Travelers is completely secured with Faremaps and
                  Faremaps.com. We are committed that the personal information of the
                  Customer/Traveler shall not be listed, traded or shared with any third party by
                  any means. If any sharing of the information or data is done, then it is going to
                  be entrusted by the respective Traveler/Customer.
                </li>
                <li>
                  In general sense of use, the personally verifiable data and information of the
                  Customers/Travelers shall be shared by the international Suppliers such as the
                  Airlines/Hotels for successful completion of the hotel bookings and flight
                  reservations and interalia.
                </li>
              </ul>

              <div className="p-4 bg-slate-50 border border-slate-200/60 rounded-2xl text-[11px] font-semibold text-slate-500 leading-normal">
                ⚙️ <strong>Third-Party Integrations:</strong> There are elite and exclusive travel
                services which are offered only by third-party service providers. Such services
                shall include credit card processing, financial analysis, call center services,
                marketing services, and fraud prevention. In all such cases, Faremaps/Faremaps.com
                would share selective information of the Customers/Travelers with these third-party
                companies.
              </div>
            </div>

            {/* How we use data */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                How Personal Information is Used
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed mb-4">
                The personal information provided by the Customer/Traveler shall be used by
                Faremaps/Faremaps.com in the following manner:
              </p>

              <ul className="space-y-3.5">
                {[
                  'To email notifications on the fresh and existing travel products and services besides the new offers;',
                  'To increase the scope of utilization of the new line of products and travel services;',
                  'To send the content and advertising material based on personal preferences and matters of interest;',
                  'To send you the Newsletter and Press releases on a periodic basis.',
                ].map((item, index) => (
                  <li
                    key={index}
                    className="flex gap-3 items-start text-slate-600 text-xs font-semibold leading-relaxed"
                  >
                    <span className="w-5 h-5 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-accent text-[10px] font-black flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="text-slate-500 text-xs font-bold pt-4 border-t border-slate-100">
                Opt-Out Choice: The Personal Information as well as the “Point of Contact” of
                Customers/Travelers may be used for marketing promotions unless Customers/Travelers
                Opt-Out from the process. If the Opt-Out option has been selected by the
                Customer/Traveler, he/she still has the advantage of visiting Faremaps/Faremaps.com,
                but in this case, the Customer/Traveler will have limited options available for
                functional aspects.
              </p>
            </div>
          </div>

          {/* Sidebar widget (Right) */}
          <div className="lg:col-span-4 sticky top-[110px] space-y-6">
            {/* VIP Desk Card */}
            {showPhoneNumber && (
              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm text-left space-y-6">
                <div>
                  <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                    Data Security Help
                  </h4>
                  <p className="text-slate-500 text-xs font-semibold leading-relaxed mb-4">
                    For questions regarding our privacy standard or to request data opt-out, speak to
                    our dedicated desk.
                  </p>

                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-3.5 bg-slate-950 hover:bg-slate-900 border border-white/10 rounded-2xl p-4 shadow-premium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-glow">
                      <span className="absolute inset-0 rounded-xl bg-accent animate-ping opacity-20"></span>
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        TOLL-FREE 24/7
                      </div>
                      <div className="text-sm font-black text-white mt-1 tracking-tight leading-none group-hover:text-accent-light transition-colors">
                        {phoneNumber}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}

              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                  Verification Promise
                </h4>
                <div className="flex items-start gap-3 bg-slate-50 border border-slate-200/60 p-4 rounded-2xl">
                  <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-slate-800 text-xs font-bold leading-tight">
                      PCI Secure & Trusted
                    </h5>
                    <p className="text-slate-400 text-[10px] font-semibold mt-1 leading-normal">
                      We never store full credit card data and use top-tier SSL/TLS layers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


      <Footer />
    </div>
  );
}
